import omit from 'lodash-es/omit';
import { useCallback } from 'react';
import { v4 as uuid } from 'uuid';

import { toast } from '@/common/components/shared/toaster/toast';
import {
  useCreateEmployeeEmergencyContactMutation,
  useDeleteEmployeeEmergencyContactMutation,
  useUpdateEmployeeEmergencyContactMutation,
} from '@/common/graphql';
import type { EmergencyContactType } from '@/common/types';
import { ApiUtils } from '@/common/utils';

interface EmergencyContactPersistence {
  employeeId: string;
  emergencyContacts: EmergencyContactType[];
  onEmergencyContactsChange: (data: EmergencyContactType[]) => void;
  // this triggers Mutation to be triggered, otherwise we let user
  // handle manually the data that was modified (e.g. to be saved later)
  autoPersistInDatabase?: boolean;
}

export const useEmergencyContactPersistence = ({
  employeeId,
  emergencyContacts = [],
  onEmergencyContactsChange,
  autoPersistInDatabase = true,
}: EmergencyContactPersistence) => {
  const [createRecord] = useCreateEmployeeEmergencyContactMutation();
  const [updateRecord] = useUpdateEmployeeEmergencyContactMutation();
  const [deleteRecord] = useDeleteEmployeeEmergencyContactMutation();

  const onEmergencyContactCreate = useCallback(
    async (formData: EmergencyContactType) => {
      if (autoPersistInDatabase) {
        await createRecord({
          variables: {
            input: {
              employeeId: employeeId,
              name: formData.name,
              phoneNumber: {
                number: formData.phoneNumber,
                countryId: formData.phoneNumberCountryCode,
              },
              relation: formData.relation,
              remarks: formData.remarks,
            },
          },
          onCompleted: data => {
            toast.success();
            onEmergencyContactsChange([
              ...omit(emergencyContacts, 'phoneNumberCountryIsdCode'),
              {
                ...formData,
                id: data.createEmployeeEmergencyContact.id,
                phoneNumberCountryIsdCode:
                  data.createEmployeeEmergencyContact.emergencyContact.phoneNumber.country?.isdCode ?? null,
              },
            ]);
          },
          onError: error => ApiUtils.handleApiError(error),
        });
      } else {
        onEmergencyContactsChange([...emergencyContacts, { ...formData, id: uuid() }]);
      }
    },
    [autoPersistInDatabase, emergencyContacts, createRecord, employeeId, onEmergencyContactsChange],
  );

  const onEmergencyContactUpdate = useCallback(
    async (formData: EmergencyContactType) => {
      if (autoPersistInDatabase) {
        await updateRecord({
          variables: {
            input: {
              id: formData.id,
              name: formData.name,
              phoneNumber: {
                number: formData.phoneNumber,
                countryId: formData.phoneNumberCountryCode,
              },
              relation: formData.relation,
              remarks: formData.remarks,
            },
          },
          onCompleted: data => {
            toast.success();
            onEmergencyContactsChange([
              ...emergencyContacts.map(item =>
                item.id === formData.id
                  ? {
                      ...omit(item, 'phoneNumberCountryIsdCode'),
                      ...formData,
                      phoneNumberCountryIsdCode:
                        data.updateEmployeeEmergencyContact.emergencyContact.phoneNumber.country?.isdCode ?? null,
                    }
                  : item,
              ),
            ]);
          },
          onError: error => ApiUtils.handleApiError(error),
        });
      } else {
        onEmergencyContactsChange([
          ...emergencyContacts.map(item => (item.id === formData.id ? { ...item, ...formData } : item)),
        ]);
      }
    },
    [autoPersistInDatabase, emergencyContacts, onEmergencyContactsChange, updateRecord],
  );

  const onEmergencyContactRemove = useCallback(
    async (id: string) => {
      if (autoPersistInDatabase) {
        await deleteRecord({
          variables: { id: id },
          onCompleted: () => {
            toast.success();
            onEmergencyContactsChange(emergencyContacts.filter(item => item.id !== id));
          },
          onError: error => ApiUtils.handleApiError(error),
        });
      } else {
        onEmergencyContactsChange(emergencyContacts.filter(item => item.id !== id));
      }
    },
    [autoPersistInDatabase, emergencyContacts, deleteRecord, onEmergencyContactsChange],
  );

  return {
    onEmergencyContactCreate,
    onEmergencyContactUpdate,
    onEmergencyContactRemove,
  };
};
